import React from 'react';
import { LuCircleCheck, LuCircleX, LuLoader, LuX } from 'react-icons/lu';

import { getStepLabel, PipelineRun, usePipelineEvents } from '../shared/hooks/use-pipeline-events';

function RunIcon({ status }: { status: PipelineRun['status'] }) {
  if (status === 'completed') return <LuCircleCheck className="text-success shrink-0" size={16} />;
  if (status === 'failed') return <LuCircleX className="text-error shrink-0" size={16} />;
  return <LuLoader className="text-primary shrink-0 animate-spin" size={16} />;
}

function RunLabel({ run }: { run: PipelineRun }) {
  const running = run.steps.find((s) => s.status === 'running');
  if (run.status === 'completed') return <span className="text-success">Sync complete</span>;
  if (run.status === 'failed') return <span className="text-error">{run.error ?? 'Sync failed'}</span>;
  if (running) return <span className="opacity-80">{getStepLabel(running.stepId)}</span>;
  return <span className="opacity-60">Starting…</span>;
}

interface Props {
  isOpened: boolean;
}

const PipelineProgress: React.FC<Props> = ({ isOpened }) => {
  const { runs, dismiss } = usePipelineEvents();

  if (runs.length === 0) return null;

  if (!isOpened) {
    // Collapsed sidebar: show a single icon for the most relevant run
    const active = runs.find((r) => r.status === 'running') ?? runs[0];
    return (
      <div
        className="flex justify-center py-2"
        title={
          active.status === 'failed'
            ? (active.error ?? 'Sync failed')
            : active.status === 'completed'
              ? 'Sync complete'
              : 'Pipeline running…'
        }
      >
        <RunIcon status={active.status} />
      </div>
    );
  }

  return (
    <div className="px-3 space-y-2 pb-2">
      {runs.map((run) => {
        const totalSteps = run.steps.length;
        const doneSteps = run.steps.filter((s) => s.status === 'succeeded' || s.status === 'skipped').length;
        const progressPct = totalSteps > 0 ? Math.round((doneSteps / totalSteps) * 100) : 0;

        return (
          <div
            key={run.runId}
            className={[
              'rounded-xl p-3 text-xs space-y-2',
              run.status === 'failed' ? 'bg-error/10 border border-error/30' : 'bg-base-200',
            ].join(' ')}
          >
            <div className="flex items-center gap-2">
              <RunIcon status={run.status} />
              <span className="flex-1 truncate font-medium leading-tight">
                <RunLabel run={run} />
              </span>
              {run.status !== 'running' && (
                <button
                  className="btn btn-ghost btn-xs p-0 min-h-0 h-auto opacity-50 hover:opacity-100"
                  onClick={() => dismiss(run.runId)}
                >
                  <LuX size={12} />
                </button>
              )}
            </div>

            {run.status === 'running' && totalSteps > 0 && (
              <progress className="progress progress-primary w-full h-1" value={progressPct} max={100} />
            )}

            {run.status === 'completed' && (
              <progress className="progress progress-success w-full h-1" value={100} max={100} />
            )}

            {run.status === 'failed' && run.error && <p className="text-error/80 leading-tight">{run.error}</p>}
          </div>
        );
      })}
    </div>
  );
};

export default PipelineProgress;
